import {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import {
  useLiveSearchAutoSubmitMutation,
  useLiveSearchFillMutation,
  useLiveSearchMutation,
} from '../../services/live-search/liveSearchQueries';
import {
  DEFAULT_HIDE_CONDITIONS_STRATEGY,
  getVisibleFieldIds,
} from '../form/formUseCase';
import {
  applyLiveSearchFields,
  buildLiveSearchRequest,
  getLiveSearchActionMode,
  getLiveSearchActionUrl,
  getLiveSearchFieldIds,
  getLiveSearchQueryTerms,
  hasFormLiveSearch,
  serializeLiveSearchFieldValue,
  shouldRunFormLiveSearch,
} from './liveSearchUseCase';

/** @type {number} */
const LIVE_SEARCH_DEBOUNCE_MS = 400;

/**
 * @param {string[]} fieldIds
 * @param {unknown} watchedValues - useWatch result (scalar or array)
 * @returns {string}
 */
function buildLiveSearchValuesKey(fieldIds, watchedValues) {
  if (fieldIds.length === 0) return '';

  const entries = fieldIds.length === 1
    ? [[fieldIds[0], watchedValues]]
    : fieldIds.map((fieldId, index) => {
      const raw = Array.isArray(watchedValues) ? watchedValues[index] : undefined;
      return [fieldId, raw];
    });

  return JSON.stringify(
    entries.map(([fieldId, raw]) => [fieldId, serializeLiveSearchFieldValue(raw)]),
  );
}

/**
 * Live search for form side panel — watches enabled fields and loads result cards.
 * @param {object} params
 * @param {ApiFormResponse | undefined} params.formConfig
 * @param {FormLayout} params.layout
 * @param {import('react-hook-form').Control<Record<string, unknown>>} params.control
 * @param {import('react-hook-form').UseFormGetValues<Record<string, unknown>>} params.getValues
 * @param {import('react-hook-form').UseFormSetValue<Record<string, unknown>>} params.setValue
 * @param {string} params.visibleFieldIdsKey - Serialized visible field ids (stable across watch() reference churn)
 * @param {boolean} params.isLoading
 * @param {(data: ApiFormPostResponse) => void} [params.onAutoSubmitSuccess]
 * @param {(error: Error) => void} [params.onAutoSubmitError]
 * @param {(error: unknown) => void} [params.onFillError]
 * @param {(url: string) => void} [params.onEditSendFormUrl]
 * @param {(params: ApiFormSubmissionField[]) => void} [params.onEditParams]
 * @returns {{
 *   results: ApiLiveSearchResult[],
 *   isPending: boolean,
 *   hasLiveSearch: boolean,
 *   isActionPending: boolean,
 *   queryTerms: string[],
 *   handleActionClick: (resultId: string, actionIndex: number) => void,
 * }}
 */
function useFormLiveSearch({
  formConfig,
  layout,
  control,
  getValues,
  setValue,
  visibleFieldIdsKey,
  isLoading,
  onAutoSubmitSuccess,
  onAutoSubmitError,
  onFillError,
  onEditSendFormUrl,
  onEditParams,
}) {
  const hasLiveSearch = hasFormLiveSearch(formConfig);
  const searchUrl = formConfig?.searchUrl;
  const liveSearchFieldIds = useMemo(
    () => (formConfig ? getLiveSearchFieldIds(formConfig) : []),
    [formConfig],
  );

  // Scoped subscription: only re-renders when a live search field changes.
  const watchedValues = useWatch({
    control,
    name: liveSearchFieldIds,
    disabled: !hasLiveSearch || liveSearchFieldIds.length === 0,
  });

  const liveSearchValuesKey = useMemo(
    () => buildLiveSearchValuesKey(liveSearchFieldIds, watchedValues),
    [liveSearchFieldIds, watchedValues],
  );

  const { dirtyFields } = useFormState({
    control,
    disabled: !hasLiveSearch,
  });

  const debounceRef = useRef(/** @type {ReturnType<typeof setTimeout> | undefined} */ (undefined));

  const {
    mutate,
    data,
    isPending,
    reset,
  } = useLiveSearchMutation();

  const {
    mutate: mutateAutoSubmit,
    isPending: isAutoSubmitPending,
  } = useLiveSearchAutoSubmitMutation({
    onSuccess: onAutoSubmitSuccess,
    onError: onAutoSubmitError,
  });

  const {
    mutate: mutateFill,
    isPending: isFillPending,
  } = useLiveSearchFillMutation();

  useEffect(() => {
    if (!hasLiveSearch || isLoading || !formConfig || !searchUrl) {
      return undefined;
    }

    const formValues = /** @type {Record<string, unknown>} */ (getValues());
    const visibleFieldIds = getVisibleFieldIds(
      formConfig,
      formValues,
      DEFAULT_HIDE_CONDITIONS_STRATEGY,
    );
    const body = buildLiveSearchRequest(formConfig, formValues, visibleFieldIds);

    if (!shouldRunFormLiveSearch({ dirtyFields, liveSearchFieldIds, body })) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      reset();
      return undefined;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const latestFormValues = /** @type {Record<string, unknown>} */ (getValues());
      const latestVisibleFieldIds = getVisibleFieldIds(
        formConfig,
        latestFormValues,
        DEFAULT_HIDE_CONDITIONS_STRATEGY,
      );
      const latestBody = buildLiveSearchRequest(
        formConfig,
        latestFormValues,
        latestVisibleFieldIds,
      );

      if (!shouldRunFormLiveSearch({
        dirtyFields,
        liveSearchFieldIds,
        body: latestBody,
      })) {
        reset();
        return;
      }

      mutate({ searchUrl, body: latestBody });
    }, LIVE_SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [
    hasLiveSearch,
    isLoading,
    formConfig,
    searchUrl,
    liveSearchValuesKey,
    visibleFieldIdsKey,
    dirtyFields,
    liveSearchFieldIds,
    getValues,
    mutate,
    reset,
  ]);

  const results = data?.results ?? [];
  const isActionPending = isAutoSubmitPending || isFillPending;

  const queryTerms = useMemo(() => {
    if (!hasLiveSearch || !formConfig) return [];

    const formValues = /** @type {Record<string, unknown>} */ (getValues());
    const visibleFieldIds = getVisibleFieldIds(
      formConfig,
      formValues,
      DEFAULT_HIDE_CONDITIONS_STRATEGY,
    );
    const body = buildLiveSearchRequest(formConfig, formValues, visibleFieldIds);
    return getLiveSearchQueryTerms(body);
  }, [hasLiveSearch, formConfig, liveSearchValuesKey, visibleFieldIdsKey, getValues]);

  const handleActionClick = useCallback((resultId, actionIndex) => {
    if (!formConfig || isActionPending) return;

    const result = results.find((item) => item.id === resultId);
    const action = result?.actions[actionIndex];
    if (!action) return;

    const mode = getLiveSearchActionMode(action);
    const url = getLiveSearchActionUrl(action);

    if (mode === 'autoSubmit') {
      if (!url) return;
      mutateAutoSubmit(url);
      return;
    }

    if (mode === 'fill') {
      if (!url) return;
      mutateFill(url, {
        onSuccess: (fillData) => {
          applyLiveSearchFields({
            fields: fillData.fields,
            setValue,
            layout,
          });
          if (fillData.editSendFormUrl) {
            onEditSendFormUrl?.(fillData.editSendFormUrl);
          }
          if (fillData.params != null) {
            onEditParams?.(fillData.params);
          }
        },
        onError: onFillError,
      });
    }
  }, [
    formConfig,
    isActionPending,
    layout,
    mutateAutoSubmit,
    mutateFill,
    onEditSendFormUrl,
    onEditParams,
    onFillError,
    results,
    setValue,
  ]);

  return {
    results,
    isPending,
    hasLiveSearch,
    isActionPending,
    queryTerms,
    handleActionClick,
  };
}

export default useFormLiveSearch;
